import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Megaphone, Send } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useBrandApi } from '../../../common/hooks/use-brand-api.hook';
import { ConfirmActionDialog, EmptyState, SearchField, SectionCard, formatDateTime } from '../../../components/design-system/admin';
import { Thumb, ThumbStack } from '../../../components/design-system/analytics';
import { useLoader } from '../../../modules/vendor/analytics/viz/use-loader';
import { BrandPage, LoadFailed, useBrandMe } from '../../../modules/brand/components/BrandUi';

/**
 * Tell every retailer that carries the brand about something — a new
 * collection, a new size, a range being discontinued. It lands in their
 * notification bell; the brand sees what it sent and to how many shops,
 * never which.
 */
export function BrandAnnouncementsPage() {
    const { t } = useTranslation();
    const api = useBrandApi();
    const { me } = useBrandMe();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [picked, setPicked] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [sending, setSending] = useState(false);

    const { data, loading, error, reload } = useLoader(async () => {
        if (!me?.connected) return null;
        const [history, distribution] = await Promise.all([api.announcements(), api.distribution('30d')]);
        return { history, distribution };
    }, [me?.connected]);

    const limits = data?.history.limits;
    const products = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (data?.distribution.products ?? []).filter((p) => !q || p.name.toLowerCase().includes(q) || (p.collectionName ?? '').toLowerCase().includes(q));
    }, [data, search]);
    const recipients = data?.distribution.retailers.total ?? 0;
    const canSend = title.trim().length > 0 && body.trim().length > 0 && recipients > 0 && !sending;

    const send = async () => {
        setSending(true);
        try {
            const sent = await api.announce({ title: title.trim(), body: body.trim(), networkProductUuids: picked });
            toast.success(t('BRAND_PORTAL.ANNOUNCE.SENT', { count: sent.delivered }));
            setTitle('');
            setBody('');
            setPicked([]);
            setConfirming(false);
            reload();
        } catch (e: any) {
            toast.error(e?.message || t('BRAND_PORTAL.ANNOUNCE.FAILED'));
        } finally {
            setSending(false);
        }
    };

    const toggle = (uuid: string) => setPicked((list) => (list.includes(uuid) ? list.filter((u) => u !== uuid) : [...list, uuid]));

    return (
        <BrandPage testId="brand-announcements" crumb={t('BRAND_PORTAL.NAV.ANNOUNCEMENTS')} title={t('BRAND_PORTAL.ANNOUNCE.TITLE')} description={t('BRAND_PORTAL.ANNOUNCE.SUBTITLE')}>
            {error && <LoadFailed onRetry={reload} />}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                <SectionCard className="min-w-0 lg:col-span-3" title={t('BRAND_PORTAL.ANNOUNCE.COMPOSE')} description={t('BRAND_PORTAL.ANNOUNCE.AUDIENCE', { count: recipients })} testId="brand-announce-compose">
                    <div className="flex flex-col gap-4">
                        <label className="flex flex-col gap-1.5">
                            <span className="text-xs font-medium text-muted-foreground">{t('BRAND_PORTAL.ANNOUNCE.FIELD_TITLE')}</span>
                            <Input value={title} maxLength={limits?.title ?? 120} onChange={(e) => setTitle(e.target.value)} placeholder={t('BRAND_PORTAL.ANNOUNCE.TITLE_PLACEHOLDER')} data-testid="brand-announce-title" />
                        </label>
                        <label className="flex flex-col gap-1.5">
                            <span className="text-xs font-medium text-muted-foreground">{t('BRAND_PORTAL.ANNOUNCE.FIELD_BODY')}</span>
                            <Textarea value={body} rows={5} maxLength={limits?.body ?? 2000} onChange={(e) => setBody(e.target.value)} placeholder={t('BRAND_PORTAL.ANNOUNCE.BODY_PLACEHOLDER')} data-testid="brand-announce-body" />
                            <span className="text-end text-xs text-muted-foreground tabular-nums">{body.length} / {limits?.body ?? 2000}</span>
                        </label>
                        <div className="flex flex-col gap-2">
                            <span className="text-xs font-medium text-muted-foreground">{t('BRAND_PORTAL.ANNOUNCE.FIELD_PRODUCTS', { count: picked.length })}</span>
                            <SearchField value={search} onChange={setSearch} placeholder={t('BRAND_PORTAL.ANNOUNCE.SEARCH_PRODUCTS')} />
                            <ul className="max-h-64 overflow-y-auto rounded-lg border">
                                {products.map((p) => (
                                    <li key={p.uuid}>
                                        <label className={cn('flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-accent/40', picked.includes(p.uuid) && 'bg-accent/30')}>
                                            <Checkbox checked={picked.includes(p.uuid)} onCheckedChange={() => toggle(p.uuid)} />
                                            <Thumb src={p.thumbnailUrl} size="xs" />
                                            <span className="min-w-0 flex-1 truncate text-sm">{p.name}</span>
                                            <span className="truncate text-xs text-muted-foreground">{p.collectionName}</span>
                                        </label>
                                    </li>
                                ))}
                                {!products.length && <li className="px-3 py-4 text-center text-xs text-muted-foreground">{loading ? '' : t('BRAND_PORTAL.ANNOUNCE.NO_PRODUCTS')}</li>}
                            </ul>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-xs text-muted-foreground">{t('BRAND_PORTAL.ANNOUNCE.LIMIT', { count: limits?.perDay ?? 5 })}</p>
                            <Button variant="outline" disabled={!canSend} onClick={() => setConfirming(true)} data-testid="brand-announce-send">
                                <Send className="size-4 rtl:-scale-x-100" />
                                {t('BRAND_PORTAL.ANNOUNCE.SEND')}
                            </Button>
                        </div>
                    </div>
                </SectionCard>

                <SectionCard className="min-w-0 lg:col-span-2" title={t('BRAND_PORTAL.ANNOUNCE.HISTORY')} testId="brand-announce-history">
                    {data?.history.data.length ? (
                        <ul className="flex flex-col divide-y">
                            {data.history.data.map((a) => (
                                <li key={a.uuid} className="py-3 first:pt-0 last:pb-0">
                                    <p className="text-sm font-medium">{a.title}</p>
                                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{a.body}</p>
                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                        {a.products.length > 0 && <ThumbStack size="xs" items={a.products.map((p) => ({ key: p.uuid, src: p.thumbnailUrl, alt: p.name ?? '' }))} />}
                                        <span>{t('BRAND_PORTAL.ANNOUNCE.REACHED', { count: a.delivered, total: a.recipients })}</span>
                                        <span>· {formatDateTime(a.createdAt)}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <EmptyState icon={<Megaphone />} title={t('BRAND_PORTAL.ANNOUNCE.EMPTY_TITLE')} description={t('BRAND_PORTAL.ANNOUNCE.EMPTY_TEXT')} />
                    )}
                </SectionCard>
            </div>

            <Card className="py-3">
                <CardContent className="text-xs text-muted-foreground">{t('BRAND_PORTAL.ANNOUNCE.PRIVACY')}</CardContent>
            </Card>

            <ConfirmActionDialog
                open={confirming}
                onOpenChange={setConfirming}
                title={t('BRAND_PORTAL.ANNOUNCE.CONFIRM_TITLE', { count: recipients })}
                description={t('BRAND_PORTAL.ANNOUNCE.CONFIRM_TEXT')}
                confirmLabel={t('BRAND_PORTAL.ANNOUNCE.SEND')}
                busy={sending}
                onConfirm={send}
            />
        </BrandPage>
    );
}

export default BrandAnnouncementsPage;
